import * as Three from 'three';
import { generateLocalEnvMap } from '../utils/generate-local-env-map';
import { modelLoader, resourceCache, loadTexture } from '../utils/loaders';
import { extractVariable } from '../../wtl-schema/helpers/extract-variable';
import { materialPresets } from '../../src/data/common/materials';
import { paintingContexts, getRandomContextId } from '../../src/data/common/contexts';

import { SubdivisionModifier } from '../utils/subdivision-modifier';
import { getFileAsBase64 } from '../../src/components/variable-input';

export let globalPointLightLimit = 0;

export const unsetLights = () => {
  globalPointLightLimit = 0;
};

const generateMaterial = (branch) => {
  let texture = null;
  let sharpenTexture = false;
  let pbrMap = null;
  let clearcoatMap = null;
  let displacementMap = null;
  let displacementScale = null;
  let normalMap = null;
  let refractionEnabled = false;
  let lightMap = null;
  let lightSrc = null;
  let lightIntensity = null;
  let color = null;
  let roughness = null;
  let metalness = null;
  let clearcoat = null;
  let clearcoatRoughness = null;
  let opacity = null;

  if (branch.materialId === 'custom') {
    texture = loadTexture(branch.textureUrl, branch.color);
    sharpenTexture = branch.sharpenTexture || false;
    pbrMap = loadTexture(branch.pbrUrl);
    clearcoatMap = loadTexture(branch.clearcoatUrl);
    displacementMap = loadTexture(branch.displacementUrl);
    displacementScale = branch.displacementScale;
    normalMap = loadTexture(branch.normalsUrl);
    refractionEnabled = branch.refractive;
    lightMap = loadTexture(branch.lightUrl);
    lightSrc = branch.lightSrc;
    lightIntensity = branch.lightIntensity;
    color = branch.color;
    roughness = branch.roughness;
    metalness = branch.metalness;
    opacity = typeof branch.opacity !== 'undefined' ? branch.opacity : (branch.opacity || 1.0);
    clearcoat = branch.clearcoat;
    clearcoatRoughness = branch.clearcoatRoughness;
  } else if (branch.materialId) {
    let preset = materialPresets[branch.materialId];

    if (!preset) {
      preset = materialPresets["wallpaper-glossy-black"];

      console.info('material unavailable', branch.materialId);
    }

    texture = loadTexture(preset.textureUrl, preset.color);
    sharpenTexture = preset.sharpenTexture || false;
    pbrMap = loadTexture(preset.pbrUrl);
    clearcoatMap = loadTexture(preset.clearcoatUrl);
    displacementMap = loadTexture(preset.displacementUrl);
    displacementScale = preset.displacementScale;
    normalMap = loadTexture(preset.normalsUrl);
    refractionEnabled = preset.refractive;
    lightMap = loadTexture(preset.lightUrl);
    lightSrc = preset.lightSrc;
    lightIntensity = preset.lightIntensity;
    color = preset.color;
    roughness = preset.roughness;
    metalness = preset.metalness;
    opacity = typeof preset.opacity !== 'undefined' ? preset.opacity : (branch.opacity || 1.0);
    clearcoat = preset.clearcoat;
    clearcoatRoughness = preset.clearcoatRoughness;
  }

  const materialType = (clearcoatMap || clearcoat || clearcoatRoughness) ? 'MeshPhysicalMaterial' : 'MeshStandardMaterial';

  return new Three[materialType]({
    aoMap: pbrMap,
    emissive: lightSrc ? new Three.Color(lightSrc) : new Three.Color(0x000000),
    emissiveMap: lightMap,
    emissiveIntensity: lightIntensity || 1.0,
    metalnessMap: pbrMap,
    normalMap: normalMap,
    roughnessMap: pbrMap,
    clearcoatMap: clearcoatMap,
    clearcoatRoughnessMap: clearcoatMap,
    displacementMap: displacementMap,
    displacementScale: displacementScale,
    opacity: opacity,
    transparent: opacity < 1,
    map: texture,
    color: new Three.Color(color || '#ffffff'),
    roughness: pbrMap ? 1 : (roughness || .5),
    metalness: pbrMap ? 1 : (metalness || .5),
    refractionRatio: refractionEnabled ? 0.975 : 1.0,
    clearcoat: clearcoatMap ? 1.0 : clearcoat,
    clearcoatRoughness: clearcoatMap ? undefined : clearcoatRoughness
  });
}

const createCube = (branch) => {
  const { sizeX, sizeY, sizeZ } = branch;
  const geometry = new Three.BoxBufferGeometry(sizeX, sizeY, sizeZ);
  const material = generateMaterial(branch);

  return new Three.Mesh(geometry, material);
}

const createPlane = (branch) => {
  const { sizeX, sizeZ } = branch;
  const geometry = new Three.PlaneBufferGeometry(sizeX, sizeZ);
  const material = generateMaterial(branch);

  return new Three.Mesh(geometry, material);
}

const createSphere = (branch) => {
  const { radius, sphereDetail } = branch;
  const detail = sphereDetail === 'high' ? 512 : 32;
  const geometry = new Three.SphereBufferGeometry(radius || 1.0, detail, detail);
  const material = generateMaterial(branch);
  return new Three.Mesh(geometry, material);
}

const createImage = ({ sprite, imageSrc, sizeX, sizeZ }) => {
  const loader = new Three.TextureLoader();
  const image = loader.load(imageSrc);

  if (sprite) {
    const material = new Three.SpriteMaterial({
      map: image,
      color: 0xffffff
    });
    const sprite = new Three.Sprite(material);

    sprite.center.set(0.0, 1.0);
    sprite.scale.set(sizeX, sizeZ, 1.0);

    return sprite;
  } else {
    const geometry = new Three.PlaneBufferGeometry(sizeX, sizeZ);
    const material = new Three.MeshBasicMaterial({
      map: image,
      transparent: true,
      color: 0xffffff,
      side: Three.DoubleSide
    });

    return new Three.Mesh(geometry, material);
  }
}

const createArch = (branch, context) => {
  const { renderQuality } = context;
  const { sizeX, sizeY, sizeZ, archX1, archY1, archX2, archY2 } = branch;
  const shape = new Three.Shape();

  shape.moveTo(0, 0);
  shape.lineTo(0, 1);
  shape.lineTo(1, 1);
  shape.lineTo(1, 1 - archY1);
  shape.lineTo(archX1, 1 - archY1);
  shape.quadraticCurveTo(
    archX2, 1 - archY1,
    archX2, 1 - archY2
  );
  shape.lineTo(archX2, 0);
  shape.lineTo(0, 0);
  const geometry = new Three.ExtrudeBufferGeometry(shape, {
    steps: 1,
    depth: sizeZ,
    bevelEnabled: false,
    curveSegments: [2, 12, 12][renderQuality || 0]
  });
  geometry.scale(sizeX, sizeY, 1);
  geometry.translate(-sizeX / 2, -sizeY / 2, -sizeZ / 2);
  const material = generateMaterial(branch);

  return new Three.Mesh(geometry, material);
}

const createWall = (branch, context) => {
  const { renderQuality } = context;
  const {
    sizeX, sizeY, sizeZ,
    holeX1, holeY1,
    holeX2, holeY2,
    holeX3, holeY3,
    holeX4, holeY4,
  } = branch;
  const shapes = [];
  const cutout = new Three.Path();

  const edges = [true, true, true, true];
  const holePoints = [true, true, true, true];

  if (holeX1 === 0 && holeY1 === 1) {
    edges[0] = holePoints[0] = false;
  }

  if (holeX2 === 1 && holeY2 === 1) {
    edges[1] = holePoints[1] = false;
  }

  if (holeX3 === 1 && holeY3 === 0) {
    edges[2] = holePoints[2] = false;
  }

  if (holeX4 === 0 && holeY4 === 0) {
    edges[3] = holePoints[3] = false;
  }
  
  let shape;

  const drawEdge = (edgeId, edge, nextEdge, holeA, holeB, parallelEdgeCase) => {
    const prevId = edgeId - 1 < 0 ? 3 : edgeId - 1;
    const nextId = edgeId + 1 > 3 ? 0 : edgeId + 1;

    const points = [
      edges[prevId] || edges[edgeId] || edges[nextId] ? edge : false,
      holePoints[edgeId] ? holeA : false,
      (holePoints[nextId] || !edges[nextId]) && !parallelEdgeCase ? holeB : false,
      edges[nextId] ? nextEdge : false,
    ].filter(Boolean);

    if (points.length > 2) {
      shape = new Three.Shape();

      points.forEach(([ x, y ], index) => shape[index === 0 ? 'moveTo' : 'lineTo'](x, y));
      shape.lineTo(points[0][0], points[0][1]);

      shapes.push(shape);
    }
  }

  drawEdge(0, [0, 1], [1, 1], [holeX1, holeY1], [holeX2, holeY2], holeY1 === 1 && holeY2 === 1);
  drawEdge(1, [1, 1], [1, 0], [holeX2, holeY2], [holeX3, holeY3], holeX2 === 1 && holeX3 === 1);
  drawEdge(2, [1, 0], [0, 0], [holeX3, holeY3], [holeX4, holeY4], holeY3 === 0 && holeY4 === 0);
  drawEdge(3, [0, 0], [0, 1], [holeX4, holeY4], [holeX1, holeY1], holeX4 === 0 && holeX1 === 0);

  const geometry = new Three.ExtrudeBufferGeometry(shapes, {
    steps: 1,
    depth: sizeZ,
    bevelEnabled: false,
    curveSegments: [2, 12, 12][renderQuality || 0]
  });
  geometry.scale(sizeX, sizeY, 1);
  geometry.translate(-sizeX / 2, -sizeY / 2, -sizeZ / 2);
  const material = generateMaterial(branch);

  if (material.displacementMap) {
    const subdivideModifier = new SubdivisionModifier(5, true);
    const modifiedGeometry = subdivideModifier.modify(new Three.Geometry().fromBufferGeometry(geometry));

    return new Three.Mesh(new Three.BufferGeometry().fromGeometry(modifiedGeometry), material);
  } else {
    return new Three.Mesh(geometry, material);
  }
}

export const parseObject = (branch, context, options) => {
  const { _selfKey, parent, isOnActiveFloor } = options;
  const {
    positionX = 0,
    positionY = 0,
    positionZ = 0,
    rotationX = 0,
    rotationY = 0,
    rotationZ = 0,
    opacity = 1.0,
    primitive,
    modelUrl,
    previewUrl,
    modelScale = 1.0,
    details
  } = branch;
  const { renderQuality, camera } = context;

  if (!parent) {
    return;
  }

  let instance = parent.getObjectByName(_selfKey);

  if (!instance) {
    let richBranch = {
      ...branch,
      materialId: (details && details.materialOptions) ? details.materialOptions[branch.materialId || 0] : branch.materialId
    };

    const material = generateMaterial(richBranch);
    const materialSD = new Three.MeshStandardMaterial({
      color: (material || {}).color || new Three.Color(0xffffff),
      roughness: .7,
      metalness: .2
    });

    if (modelUrl) {
      const placeholderSprite = new Three.Sprite(new Three.SpriteMaterial({
        map: loadTexture(previewUrl),
        side: Three.DoubleSide,
      }));

      placeholderSprite.center.set(0.5, 0);
      placeholderSprite.scale.setScalar(0.0);

      if (isOnActiveFloor === false && renderQuality < 2) {
        instance = new Three.Group();
        instance.add(placeholderSprite.clone());

        parent.add(instance);
      } else {
        instance = new Three.LOD();

        const instanceLOD3 = new Three.Group();
        const instanceLOD2 = new Three.Group();

        instanceLOD3.add(placeholderSprite.clone());
        instanceLOD2.add(placeholderSprite.clone());

        instance.addLevel(instanceLOD3, 0);

        if (renderQuality < 2) {
          instance.addLevel(instanceLOD2, 400);
        }

        const escapedModelUrl = extractVariable(modelUrl);
        let promised = resourceCache[escapedModelUrl];

        if (!promised) {
          promised = resourceCache[escapedModelUrl] = new Promise((done) => {
            const extension = escapedModelUrl.split('.').splice(-1);
            const loader = (modelLoader[extension || 'obj']);

            if (typeof loader === 'function') {
              loader().load(
                escapedModelUrl,
                (model) => {
                  resourceCache[escapedModelUrl] = new Promise(_done => _done(model));

                  done(model);
                },
                undefined,
                () => console.warn(`failed to load model: ${escapedModelUrl}`)
              );
            }
          });
        }

        promised.then((model) => {
          instanceLOD3.children.forEach(child => instanceLOD3.remove(child));
          instanceLOD2.children.forEach(child => instanceLOD2.remove(child));

          instanceLOD3.add(model.clone());
          instanceLOD2.add(model.clone());

          const applyToAll = (model, applyMaterial) => {
            model.children.forEach((mesh) => {
              mesh.selectable = options.selectable;

              if (!richBranch.lightSrc) {
                mesh.castShadow = true;
                mesh.receiveShadow = true;
              }

              if (mesh.name && mesh.name.match(/^glass.*/gm)) {
                mesh.material = generateMaterial({ materialId: 'glass' });
                generateLocalEnvMap(richBranch, mesh, context, { top: instanceLOD3 });
                
                mesh.castShadow = false;
                mesh.receiveShadow = false;
              } else if (mesh.name && mesh.name.match(/^context.*/gm)) {
                if (richBranch.contextType === 'painting') {
                  if (!branch.contextId) {
                    branch.contextId = getRandomContextId(paintingContexts);
                  }

                  const paintingMaterial = generateMaterial({
                    materialId: 'custom',
                    textureUrl: paintingContexts[branch.contextId || 0],
                    pbrUrl: 'https://cdn.wtlstudio.com/common-ptr.wtlstudio.com/8984591d-fdb0-4782-85da-36684c5041a8.jpg',
                    normalsUrl: 'https://cdn.wtlstudio.com/common-ptr.wtlstudio.com/819de472-6348-4446-9468-c5446eb7515d.png'
                  });
                  mesh.material = paintingMaterial;
                } else {
                  mesh.visible = false;

                  mesh.castShadow = false;
                  mesh.receiveShadow = false;
                }
              } else if (mesh.name && mesh.name.match(/^bulb.*/gm)) {            
                branch.contextLight = true;
  
                if (isOnActiveFloor) {
                  const bulb = new Three.PointLight(extractVariable(richBranch.contextColor) || 0xffffff, .95, 50, 2);
                  bulb.position.x = mesh.position.x;
                  bulb.position.y = mesh.position.y;
                  bulb.position.z = mesh.position.z;
                  bulb.name = '_ca-light-src';

                  // bulb.shadow.mapSize.width = [256, 512, 512][renderQuality || 0];
                  // bulb.shadow.mapSize.height = [256, 512, 512][renderQuality || 0];
                  // bulb.shadow.camera.near = .3;
                  // bulb.shadow.camera.far = 100.;
                  // bulb.shadow.bias = 0.00001;
                  // bulb.shadow.camera.scale.set(20, 20, 20);

                  bulb.castShadow = false;
                  bulb.visible = false;

                  instanceLOD3.add(bulb);
                }

                mesh.visible = false;
                mesh.castShadow = false;
                mesh.receiveShadow = false;
              } else {
                mesh.material = applyMaterial;
                generateLocalEnvMap(richBranch, mesh, context, { top: instanceLOD3 });
              }

              if (mesh.children) {
                applyToAll(mesh, applyMaterial);
              }
            });
          };

          applyToAll(instanceLOD3, material);
          applyToAll(instanceLOD2, materialSD.clone());

          if (!richBranch.lightSrc) {
            generateLocalEnvMap(richBranch, instanceLOD3, context);
            generateLocalEnvMap(richBranch, instanceLOD2, context);
          }

          if (renderQuality < 2) {
            instance.addLevel(placeholderSprite.clone(), 800);
          }
        });
      }
    } else {
      switch (primitive) {
        case 'sphere':
          instance = createSphere(richBranch);
        break;
        case 'plane':
          instance = createPlane(richBranch);
        break;
        case 'cube':
          instance = createCube(richBranch);
        break;
        case 'image':
          instance = createImage(richBranch);
        break;
        case 'arch':
          instance = createArch(richBranch, context);
        break;
        case 'wall':
          instance = createWall(richBranch, context);
        break;
        default:
          instance = createSphere(richBranch);
        break;
      }
    }
    
    if (primitive !== 'image' && !branch.lightSrc) {
      instance.castShadow = true;
    }
    
    instance.selectable = options.selectable;
    instance.selectionTarget = options.selectionTarget;
    instance.receiveShadow = true;

    if (opacity >= 1 && !branch.lightSrc) {
      generateLocalEnvMap(branch, instance, context);
    }

    parent.add(instance);
  }

  instance.position.x = positionX;
  instance.position.y = positionY;
  instance.position.z = positionZ;

  const bulbInstance = instance.getObjectByName('_ca-light-src');

  if (bulbInstance) {
    if (isOnActiveFloor && branch.contextLightOn) {
      // if (bulbInstance.visible === false) {
      //   globalPointLightLimit = globalPointLightLimit + 1;
      // }

      bulbInstance.visible = true;
      bulbInstance.color = new Three.Color(extractVariable(branch.contextColor) || 0xffffff);
    } 

    if (!branch.contextLightOn) {
      // if (bulbInstance.visible === true) {
      //   globalPointLightLimit = Math.max(0, globalPointLightLimit - 1);
      // }

      bulbInstance.visible = false;
    }
  }

  instance.rotation.x = 0;
  instance.rotation.y = 0;
  instance.rotation.z = 0;

  instance.rotateX(rotationX);
  instance.rotateY(rotationY);
  instance.rotateZ(rotationZ);

  instance.scale.set(modelScale || 1.0, modelScale || 1.0, modelScale || 1.0);

  return instance;
};
